import type { ComponentInterface, EventEmitter } from '@stencil/core';
import type { CheckboxChangeEventDetail } from './checkbox-interface';
import type { Attributes } from '@utils/helpers';
import { Component, Host, h, Prop, Element, Event } from '@stencil/core';

@Component({
  tag: 'jackery-checkbox',
  styleUrl: 'jackery-checkbox.css',
  shadow: true,
})
export class JackeryCheckbox implements ComponentInterface {
  private inputId = `jky-cb-${checkboxIds++}`;
  private focusEl?: HTMLElement;
  private inheritedAttributes: Attributes = {};
  @Element() el!: HTMLJackeryCheckboxElement;

  /**
   * checkbox属性name值
   */
  @Prop() name: string = this.inputId;

  /**
   * checkbox状态
   */
  @Prop({ mutable: true }) checked = false;

  /**
   * 复选框的值.
   */
  @Prop() value: any | null = 'on';

  /**
   * 禁用checkbox
   */
  @Prop() disabled = false;

  /**
   * label相对于复选框的位置：
   * `"start"`: label在复选框的右侧.
   * `"end"`: label在复选框的左侧.
   * `"fixed"`: 标签位置与`"start"`相同，但是超长文本将被省略，显示（...）.
   */
  @Prop() labelPlacement: 'start' | 'end' | 'fixed' = 'start';

  /**
   * label和checkbox在垂直方向相对位置：
   * `"start"`: label与复选框顶部对齐
   * `"center"`: label与复选框居中对齐
   */
  @Prop() alignment: 'start' | 'center' = 'start';

  /**
   * 用户操作checkbox状态更改时触发
   * 当以编程方式设置 `checked` 属性时，不会触发
   */
  @Event() jkyChange!: EventEmitter<CheckboxChangeEventDetail>;

  /**
   * checkbox focus时触发
   */
  @Event() jkyFocus!: EventEmitter<void>;

  /**
   * checkbox blur时触发
   */
  @Event() jkyBlur!: EventEmitter<void>;

  private onClick = (ev: MouseEvent) => {
    if (this.disabled) return;
    this.toggleChecked(ev);
  };

  /**
   * 设置选中并派发
   * jkyChange事件更新.
   * 检查状态响应用户
   * 比如点击操作
   */
  private setChecked = (state: boolean) => {
    const isChecked = (this.checked = state);
    this.jkyChange.emit({
      checked: isChecked,
      value: this.value,
    });
  };

  private toggleChecked = (ev: Event) => {
    ev.preventDefault();

    this.setFocus();
    this.setChecked(!this.checked);
  };

  private setFocus() {
    if (this.focusEl) {
      this.focusEl.focus();
    }
  }

  private onFocus = () => {
    this.jkyFocus.emit();
  };

  private onBlur = () => {
    this.jkyBlur.emit();
  };

  private getSVGPath(): HTMLElement {
    let path = (
      <path d="M1.73,12.91 8.1,19.28 22.79,4.59" part="mark" />
    );
    return path;
  }

  render() {
    const {inputId, el, checked, disabled, inheritedAttributes, getSVGPath, labelPlacement, alignment} = this
    const path = getSVGPath();

    return (
      <Host
        onClick={this.onClick}
        class={{
          'checkbox-checked': checked,
          'checkbox-disabled': disabled,
          [`checkbox-label-placement-${labelPlacement}`]: true,
          [`checkbox-label-alignment-${alignment}`]: true,
        }}
      >
        <label class="checkbox-wrapper">
          <input 
            id={inputId} 
            checked={checked ? true : undefined}
            disabled={disabled}
            onChange={this.toggleChecked}
            onFocus={() => this.onFocus()}
            onBlur={() => this.onBlur()}
            ref={(focusEl) => (this.focusEl = focusEl)}
            type="checkbox" 
            {...inheritedAttributes}
          />
          <div
            class={{
              'label-text-wrapper': true,
              'label-text-wrapper-hidden': el.textContent === '',
            }}
            part="label"
          >
            <slot></slot>
          </div>
          <div class="native-wrapper">
            <svg class="checkbox-icon" viewBox="0 0 24 24" part="container">
              {path}
            </svg>
          </div>
        </label>
      </Host>
    );
  }
}

let checkboxIds = 0;
